/**
 * /ai/quality (OCP-632) - continuous chat-model evaluation.
 *
 * The nightly golden-set run per model: deterministic pass rate, judge
 * language score, invented entities, failure classes; the 30-day trend; the
 * run list with a drill-down into every (case, model) result incl. the raw
 * output, the checks and the judge's unsupported claims; a diff between two
 * runs for one model; the case set with an editor. "Run now" and the case
 * editor make the same calls as `ocp_run_ai_eval` / `ocp_upsert_ai_eval_case`.
 *
 * OCP-700: the "This week" section is the object the Monday digest reports
 * (`GET /v1/admin/ai/eval-week`): the fleet model as headline with its delta
 * to the week before, every other model on its own line marked candidate or
 * comparison, the cases that failed most (click = open the latest run with
 * that model, failed only), the judge's unsupported claims grouped by wording,
 * and the legend that says what each failure class means.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api';

export interface EvalModelSummary {
  model: string;
  cases: number;
  errors: number;
  deterministic_pass: number;
  deterministic_pass_pct: number;
  judge_scored: number;
  judge_avg: number | null;
  invented_entities: number;
  unsupported_claims: number;
  judge_unverified?: number;
  judge_overruled?: number;
  judge_inference?: number;
  latency_avg_ms: number | null;
  prompt_tokens: number;
  completion_tokens: number;
  failure_classes: Record<string, number>;
}

export interface EvalRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  trigger: string;
  models: string[];
  judge_model: string | null;
  case_count: number;
  status: string;
  summary: EvalModelSummary[] | null;
  error: string | null;
  created_by: string;
  case_set_version: string | null;
  reasoning_effort: string | null;
  /** OCP-679 - core-tenant = the eval tenant's chat model went through core's routes. */
  source?: 'gateway-direct' | 'core-tenant';
  core_version?: string | null;
  /** OCP-679 - the queue job of a manual run; rejudge state null | running | done | failed. */
  job_id?: string | null;
  rejudge_status?: 'running' | 'done' | 'failed' | null;
  rejudged_at?: string | null;
  rejudge_error?: string | null;
}

/** OCP-679 - runs and rejudges answer 202 and finish on the queue; the page polls the run row. */
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 400;

async function pollRun(id: string, settled: (run: EvalRun) => boolean): Promise<EvalRun | null> {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    const d = await api<{ run: EvalRun }>(`/v1/admin/ai/eval-runs/${encodeURIComponent(id)}?failed_only=true`);
    if (settled(d.run)) return d.run;
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  return null;
}

export interface EvalResult {
  id: string;
  case_key: string;
  operation: string;
  language: string;
  kind: string;
  model: string;
  ok: boolean;
  error: string | null;
  output: string | null;
  latency_ms: number | null;
  finish_reason: string | null;
  checks: Record<string, { pass: boolean; detail: string | null }>;
  deterministic_pass: boolean;
  invented: string[];
  judge_score: number | null;
  judge_claims: {
    supported: number;
    unsupported: string[];
    notes: string | null;
    error?: string;
    claims?: Array<{ text: string; verdict: 'supported' | 'unsupported' | 'unverified' | 'overruled' | 'inference'; evidence: string | null; why: string | null }>;
    unverified?: number;
    overruled?: number;
    inference?: number;
    prompt_version?: string;
  } | null;
  failure_classes: string[];
  /** OCP-679 - the path the case took and what core's guards did (null = unknown). */
  source?: 'gateway-direct' | 'core-route' | 'core-chat';
  core_route?: string | null;
  core_guard?: { languageRetried: boolean; detectedLanguage: string | null; structureRetried: boolean; groundingStripped: boolean; groundingRetried: boolean } | null;
  core_mode?: string | null;
}

export interface EvalCase {
  id: string;
  key: string;
  operation: string;
  language: string;
  kind: string;
  instructions: string;
  fixture: string;
  expectations: Record<string, unknown>;
  source: string;
  active: boolean;
  note: string | null;
}

export interface TrendPoint {
  day: string;
  model: string;
  cases: number;
  deterministic_pass_pct: number;
  judge_avg: number | null;
  invented_entities: number;
  failure_classes: Record<string, number>;
}

export interface WeekModel {
  model: string;
  role: 'fleet' | 'candidate' | 'comparison';
  runs: number;
  case_runs: number;
  deterministic_pass_pct: number;
  previous_pass_pct: number | null;
  delta_pct: number | null;
  judge_avg: number | null;
  invented_entities: number;
  failure_classes: Record<string, number>;
  top_failing_cases: Array<{ case_key: string; fails: number; runs: number; classes: string[] }>;
  top_unsupported_claims: Array<{ claim: string; case_key: string; count: number; why?: string | null }>;
  judge_unverified?: number;
  judge_overruled?: number;
  judge_inference?: number;
}

export interface Week {
  from: string;
  to: string;
  days: number;
  fleet_model: string;
  min_pass_pct: number;
  models: WeekModel[];
  legend: Record<string, string>;
}

interface DiffItem { case_key: string; a_pass: boolean | null; b_pass: boolean | null; a_judge: number | null; b_judge: number | null; changed: boolean }

const CARD = 'rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4';
const BTN = 'rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50';
const INPUT = 'rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm';

function fmtPct(v: number | null | undefined): string {
  return v === null || v === undefined ? '-' : `${v.toFixed(1)} %`;
}
function fmtScore(v: number | null | undefined): string {
  return v === null || v === undefined ? '-' : v.toFixed(2);
}
function fmtWhen(iso: string | null): string {
  return iso ? iso.replace('T', ' ').slice(0, 16) + 'Z' : '-';
}
function classes(fc: Record<string, number>): string {
  const e = Object.entries(fc).sort((a, b) => b[1] - a[1]);
  return e.length === 0 ? '-' : e.map(([k, v]) => `${k} x${v}`).join(', ');
}
function fmtDelta(v: number | null): string {
  return v === null ? 'no run last week' : `${v >= 0 ? '+' : ''}${v.toFixed(1)} vs last week`;
}
const ROLE_LABEL: Record<WeekModel['role'], string> = { fleet: 'fleet model', candidate: 'candidate', comparison: 'comparison run' };

/** Failure classes as chips; the legend text is the tooltip so a class is never a bare word. */
function ClassChips({ fc, legend }: { fc: Record<string, number> | string[]; legend: Record<string, string> }) {
  const entries = Array.isArray(fc) ? fc.map((k) => [k, 0] as const) : Object.entries(fc).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <span className="text-xs text-gray-500">-</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <span key={k} className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs" title={legend[k] ?? k} data-testid={`class-${k}`}>
          {k}{v > 0 ? ` x${v}` : ''}
        </span>
      ))}
    </span>
  );
}

const EMPTY_CASE = { key: '', operation: 'reply', language: 'de', kind: 'tricky', instructions: '', fixture: '', expectations: '{\n  "language": "de"\n}', source: 'correction-derived', note: '' };

export function AdminAiQualityPage() {
  const [params, setParams] = useSearchParams();
  const selectedRunId = params.get('run');
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [cases, setCases] = useState<EvalCase[]>([]);
  const [run, setRun] = useState<{ run: EvalRun; results: EvalResult[] } | null>(null);
  const [modelFilter, setModelFilter] = useState<string>('');
  const [failedOnly, setFailedOnly] = useState(false);
  const [openResult, setOpenResult] = useState<string | null>(null);
  const [diffWith, setDiffWith] = useState<string>('');
  const [diff, setDiff] = useState<DiffItem[] | null>(null);
  const [runModels, setRunModels] = useState('');
  const [runEffort, setRunEffort] = useState('');
  const [week, setWeek] = useState<Week | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [showCases, setShowCases] = useState(false);
  const [caseForm, setCaseForm] = useState({ ...EMPTY_CASE });

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, t, c, w] = await Promise.all([
        api<{ items: EvalRun[] }>('/v1/admin/ai/eval-runs?limit=40'),
        api<{ items: TrendPoint[] }>('/v1/admin/ai/eval-trend?days=30'),
        api<{ items: EvalCase[] }>('/v1/admin/ai/eval-cases?include_inactive=true'),
        api<Week>('/v1/admin/ai/eval-week?days=7'),
      ]);
      setRuns(r.items);
      setTrend(t.items);
      setCases(c.items);
      setWeek(w);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load the evaluation data (HTTP ${err.status}).` : 'Could not load the evaluation data.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedRunId) {
      setRun(null);
      return;
    }
    const q = new URLSearchParams();
    if (modelFilter) q.set('model', modelFilter);
    if (failedOnly) q.set('failed_only', 'true');
    api<{ run: EvalRun; results: EvalResult[] }>(`/v1/admin/ai/eval-runs/${encodeURIComponent(selectedRunId)}?${q.toString()}`)
      .then(setRun)
      .catch((err: unknown) => setError(err instanceof ApiError ? `Could not load the run (HTTP ${err.status}).` : 'Could not load the run.'));
  }, [selectedRunId, modelFilter, failedOnly]);

  useEffect(() => {
    if (!selectedRunId || !diffWith || !modelFilter) {
      setDiff(null);
      return;
    }
    api<{ items: DiffItem[] }>(`/v1/admin/ai/eval-runs/${encodeURIComponent(selectedRunId)}/diff/${encodeURIComponent(diffWith)}?model=${encodeURIComponent(modelFilter)}`)
      .then((d) => setDiff(d.items))
      .catch(() => setDiff(null));
  }, [selectedRunId, diffWith, modelFilter]);

  const rejudge = async (id: string) => {
    setBusy(true);
    setFlash(null);
    setError(null);
    try {
      await api<{ run: EvalRun; job_id: string | null }>(`/v1/admin/ai/eval-runs/${encodeURIComponent(id)}/rejudge`, { method: 'POST' });
      setFlash('Rejudge queued - the judge stage takes a few minutes, this page follows it.');
      const settled = await pollRun(id, (r) => r.rejudge_status === 'done' || r.rejudge_status === 'failed');
      if (!settled) setError('The rejudge is still running - reload the page later.');
      else if (settled.rejudge_status === 'failed') setError(`Rejudge failed: ${settled.rejudge_error ?? 'unknown error'}.`);
      else setFlash('Rejudge done - judge scores, claims and the summary are rewritten.');
      await load();
      setParams({ run: id });
    } catch (err) {
      setError(err instanceof ApiError ? (err.status === 409 ? 'A rejudge of this run is already in progress.' : `Rejudge failed (HTTP ${err.status}).`) : 'Rejudge failed.');
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    setFlash(null);
    setError(null);
    try {
      const models = runModels.split(',').map((m) => m.trim()).filter(Boolean);
      const body: { models?: string[]; reasoning_effort?: string } = {};
      if (models.length > 0) body.models = models;
      if (runEffort) body.reasoning_effort = runEffort;
      const res = await api<{ run: EvalRun; job_id: string | null }>('/v1/admin/ai/eval-runs', { method: 'POST', body });
      setFlash(`Run ${res.run.id.slice(0, 8)} queued - it takes a few minutes, this page follows it.`);
      const settled = await pollRun(res.run.id, (r) => r.status !== 'running');
      if (!settled) setError(`Run ${res.run.id.slice(0, 8)} is still running - reload the page later.`);
      else if (settled.status === 'failed') setError(`Run ${settled.id.slice(0, 8)} failed: ${settled.error ?? 'unknown error'}.`);
      else setFlash(`Run ${settled.id.slice(0, 8)} done. Alerts, if any, are in the notifications.`);
      await load();
      setParams({ run: res.run.id });
    } catch (err) {
      setError(err instanceof ApiError ? `The run failed (HTTP ${err.status}).` : 'The run failed.');
    } finally {
      setBusy(false);
    }
  };

  const saveCase = async () => {
    setBusy(true);
    setFlash(null);
    setError(null);
    try {
      let expectations: unknown;
      try {
        expectations = JSON.parse(caseForm.expectations);
      } catch {
        setError('Expectations must be a JSON object.');
        return;
      }
      await api('/v1/admin/ai/eval-cases', {
        method: 'POST',
        body: {
          key: caseForm.key.trim(),
          operation: caseForm.operation.trim(),
          language: caseForm.language,
          kind: caseForm.kind,
          instructions: caseForm.instructions.trim(),
          fixture: caseForm.fixture.trim(),
          expectations,
          source: caseForm.source,
          note: caseForm.note.trim() || null,
          reason: 'edited on the AI quality page',
        },
      });
      setFlash(`Case ${caseForm.key.trim()} saved.`);
      setCaseForm({ ...EMPTY_CASE });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Could not save the case (HTTP ${err.status}).` : 'Could not save the case.');
    } finally {
      setBusy(false);
    }
  };

  const toggleCase = async (c: EvalCase) => {
    setBusy(true);
    try {
      await api('/v1/admin/ai/eval-cases', {
        method: 'POST',
        body: { key: c.key, operation: c.operation, language: c.language, kind: c.kind, instructions: c.instructions, fixture: c.fixture, expectations: c.expectations, source: c.source, active: !c.active, note: c.note, reason: c.active ? 'parked on the AI quality page' : 'reactivated on the AI quality page' },
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Could not update the case (HTTP ${err.status}).` : 'Could not update the case.');
    } finally {
      setBusy(false);
    }
  };

  const models = useMemo(() => [...new Set(runs.flatMap((r) => r.models))], [runs]);
  const trendByDay = useMemo(() => {
    const days = [...new Set(trend.map((t) => t.day))].sort().reverse();
    return days.map((day) => ({ day, points: trend.filter((t) => t.day === day) }));
  }, [trend]);
  const latest = runs.find((r) => r.status === 'done' && r.summary);
  const activeCases = cases.filter((c) => c.active).length;
  const legend = week?.legend ?? {};
  const fleetWeek = week?.models.find((m) => m.role === 'fleet') ?? null;
  const openFailures = (model: string) => {
    const target = runs.find((r) => r.status === 'done' && r.models.includes(model));
    if (!target) return;
    setModelFilter(model);
    setFailedOnly(true);
    setParams({ run: target.id });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI quality</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Nightly golden-set run of the fleet chat model and the candidates: deterministic checks (language, format, no invented facts, no promises), judge grading, drift alerts. {activeCases} active cases.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input className={INPUT} placeholder="models (comma-separated, empty = fleet + candidates)" value={runModels} onChange={(e) => setRunModels(e.target.value)} style={{ minWidth: 300 }} />
          <select className={INPUT} value={runEffort} onChange={(e) => setRunEffort(e.target.value)} title="Pin the reasoning effort for every model of this run (comparison against the per-model default)" aria-label="reasoning effort">
            <option value="">default effort</option>
            <option value="none">reasoning none</option>
            <option value="low">reasoning low</option>
            <option value="medium">reasoning medium</option>
            <option value="high">reasoning high</option>
          </select>
          <button className={BTN} disabled={busy} onClick={() => void runNow()}>
            {busy ? 'Working...' : 'Run now'}
          </button>
          <button className={BTN} onClick={() => setShowCases((v) => !v)}>
            {showCases ? 'Hide cases' : 'Cases'}
          </button>
        </div>
      </div>

      {error && <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</div>}
      {flash && <div className="rounded border border-green-300 bg-green-50 dark:bg-green-900/30 px-3 py-2 text-sm text-green-800 dark:text-green-300">{flash}</div>}

      {week && (
        <div className={CARD} data-testid="week-section">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-sm font-semibold">This week ({week.days} days to {fmtWhen(week.to)})</div>
            <button className="text-xs underline" onClick={() => setShowLegend((v) => !v)}>
              {showLegend ? 'hide' : 'what the failure classes mean'}
            </button>
          </div>
          {showLegend && (
            <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs md:grid-cols-2" data-testid="class-legend">
              {Object.entries(week.legend).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <dt className="shrink-0 font-mono">{k}</dt>
                  <dd className="text-gray-600 dark:text-gray-400">{v}</dd>
                </div>
              ))}
            </dl>
          )}
          {week.models.length === 0 ? (
            <p className="mt-2 text-xs text-gray-500">No evaluation result in this window.</p>
          ) : (
            <>
              {fleetWeek ? (
                <div className="mt-3 flex flex-wrap items-end gap-6" data-testid="week-headline">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{fleetWeek.model} - fleet model</div>
                    <div className="text-3xl font-semibold tabular-nums">{fmtPct(fleetWeek.deterministic_pass_pct)}</div>
                    <div className={`text-xs ${fleetWeek.delta_pct !== null && fleetWeek.delta_pct < 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-600 dark:text-gray-400'}`}>{fmtDelta(fleetWeek.delta_pct)}{fleetWeek.previous_pass_pct !== null ? ` (${fmtPct(fleetWeek.previous_pass_pct)})` : ''}</div>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {fleetWeek.case_runs} case runs in {fleetWeek.runs} runs, judge {fmtScore(fleetWeek.judge_avg)}/5, {fleetWeek.invented_entities} invented, floor {week.min_pass_pct} %
                    {fleetWeek.deterministic_pass_pct < week.min_pass_pct && <span className="ml-2 rounded bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 text-red-700 dark:text-red-300">below floor</span>}
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-xs text-red-700 dark:text-red-300">The fleet model {week.fleet_model || '(unset)'} did not run this week.</p>
              )}
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-1">Model</th>
                    <th>Role</th>
                    <th className="text-right">Runs</th>
                    <th className="text-right">Case runs</th>
                    <th className="text-right">Pass</th>
                    <th className="text-right">Last week</th>
                    <th className="text-right">Judge</th>
                    <th>Failure classes</th>
                  </tr>
                </thead>
                <tbody>
                  {week.models.map((m) => (
                    <tr key={m.model} className="border-t border-gray-100 dark:border-gray-800 align-top" data-testid={`week-${m.model}`}>
                      <td className="py-1 text-xs">{m.model}</td>
                      <td className="text-xs">
                        <span className={`rounded px-1.5 py-0.5 ${m.role === 'fleet' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-800'}`}>{ROLE_LABEL[m.role]}</span>
                      </td>
                      <td className="text-right tabular-nums">{m.runs}</td>
                      <td className="text-right tabular-nums">{m.case_runs}</td>
                      <td className="text-right tabular-nums">{fmtPct(m.deterministic_pass_pct)}</td>
                      <td className="text-right tabular-nums text-xs">{m.previous_pass_pct === null ? '-' : `${fmtPct(m.previous_pass_pct)} (${m.delta_pct! >= 0 ? '+' : ''}${m.delta_pct!.toFixed(1)})`}</td>
                      <td className="text-right tabular-nums">{fmtScore(m.judge_avg)}</td>
                      <td className="py-1"><ClassChips fc={m.failure_classes} legend={week.legend} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 grid gap-4 lg:grid-cols-2">
                {week.models.map((m) => (
                  <div key={m.model} className="rounded border border-gray-200 dark:border-gray-800 p-2" data-testid={`week-detail-${m.model}`}>
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{m.model} - cases failing most</div>
                    {m.top_failing_cases.length === 0 ? (
                      <p className="text-xs text-gray-500">No deterministic failure this week.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <tbody>
                          {m.top_failing_cases.map((c) => (
                            <tr key={c.case_key} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="py-1 font-mono">
                                <button className="underline" onClick={() => openFailures(m.model)} title="Open the latest run of this model, failed cases only">{c.case_key}</button>
                              </td>
                              <td className="tabular-nums">{c.fails}/{c.runs} runs failed</td>
                              <td><ClassChips fc={c.classes} legend={week.legend} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div className="mt-2 text-xs font-medium uppercase tracking-wide text-gray-500">{m.model} - unsupported claims (judge opinion, after the cross-check)</div>
                    {(m.judge_overruled ?? 0) + (m.judge_unverified ?? 0) + (m.judge_inference ?? 0) > 0 && (
                      <p className="text-xs text-gray-500" data-testid={`judge-crosscheck-${m.model}`}>
                        Cross-check discarded {m.judge_unverified ?? 0} verdict{(m.judge_unverified ?? 0) === 1 ? '' : 's'} without a real quote and overruled {m.judge_overruled ?? 0} "unsupported" verdict{(m.judge_overruled ?? 0) === 1 ? '' : 's'} that were verbatim in the input.
                      {' '}Filed {m.judge_inference ?? 0} claim{(m.judge_inference ?? 0) === 1 ? '' : 's'} as inference (derived from the input, not counted as unsupported).
                      </p>
                    )}
                    {m.top_unsupported_claims.length === 0 ? (
                      <p className="text-xs text-gray-500">The judge grounded every claim this week.</p>
                    ) : (
                      <ul className="text-xs">
                        {m.top_unsupported_claims.map((c) => (
                          <li key={`${c.case_key}|${c.claim}`} className="border-t border-gray-100 dark:border-gray-800 py-1">
                            <span className="font-mono text-gray-500">{c.case_key}</span> x{c.count}: {c.claim}
                            {c.why && <span className="ml-1 rounded bg-gray-100 dark:bg-gray-800 px-1 text-[10px] uppercase tracking-wide text-gray-500" title="The judge's reason">{c.why}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {latest?.summary && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {latest.summary.map((m) => (
            <div key={m.model} className={CARD} data-testid={`summary-${m.model}`}>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{m.model}</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">{fmtPct(m.deterministic_pass_pct)}</div>
              <div className="text-xs text-gray-600">
                {m.deterministic_pass}/{m.cases} cases pass, judge {fmtScore(m.judge_avg)}/5, {m.invented_entities} invented, {m.unsupported_claims} unsupported, {m.latency_avg_ms ?? '-'} ms
              </div>
              <div className="mt-1"><ClassChips fc={m.failure_classes} legend={legend} /></div>
              <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">latest run {fmtWhen(latest.started_at)}</div>
            </div>
          ))}
        </div>
      )}

      {showCases && (
        <div className={CARD}>
          <div className="text-sm font-semibold">Golden cases</div>
          <p className="text-xs text-gray-500">Synthetic or anonymised only, never customer data. A correction becomes a case by pasting the anonymised input as fixture, the task as instructions, and the checks the corrected output must satisfy as expectations (same shape as `ocp_upsert_ai_eval_case`).</p>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            <input className={INPUT} placeholder="key, e.g. reply.de.refund-question" value={caseForm.key} onChange={(e) => setCaseForm({ ...caseForm, key: e.target.value })} />
            <input className={INPUT} placeholder="operation, e.g. reply" value={caseForm.operation} onChange={(e) => setCaseForm({ ...caseForm, operation: e.target.value })} />
            <div className="flex gap-2">
              <select className={INPUT} value={caseForm.language} onChange={(e) => setCaseForm({ ...caseForm, language: e.target.value })}>
                <option value="de">de</option>
                <option value="en">en</option>
              </select>
              <select className={INPUT} value={caseForm.kind} onChange={(e) => setCaseForm({ ...caseForm, kind: e.target.value })}>
                <option value="sunny">sunny</option>
                <option value="tricky">tricky</option>
              </select>
              <select className={INPUT} value={caseForm.source} onChange={(e) => setCaseForm({ ...caseForm, source: e.target.value })}>
                <option value="correction-derived">correction-derived</option>
                <option value="anonymised-own-workspace">anonymised-own-workspace</option>
                <option value="synthetic">synthetic</option>
              </select>
            </div>
            <input className={INPUT} placeholder="note (optional)" value={caseForm.note} onChange={(e) => setCaseForm({ ...caseForm, note: e.target.value })} />
            <textarea className={`${INPUT} md:col-span-2`} rows={3} placeholder="instructions (the task)" value={caseForm.instructions} onChange={(e) => setCaseForm({ ...caseForm, instructions: e.target.value })} />
            <textarea className={`${INPUT} md:col-span-2`} rows={5} placeholder="fixture (the anonymised input)" value={caseForm.fixture} onChange={(e) => setCaseForm({ ...caseForm, fixture: e.target.value })} />
            <textarea className={`${INPUT} md:col-span-2 font-mono`} rows={5} placeholder="expectations JSON" value={caseForm.expectations} onChange={(e) => setCaseForm({ ...caseForm, expectations: e.target.value })} />
          </div>
          <div className="mt-2">
            <button className={BTN} disabled={busy || !caseForm.key.trim() || !caseForm.instructions.trim() || !caseForm.fixture.trim()} onClick={() => void saveCase()}>
              Save case
            </button>
          </div>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-1">Key</th>
                <th>Operation</th>
                <th>Lang</th>
                <th>Kind</th>
                <th>Source</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1 font-mono text-xs">{c.key}</td>
                  <td>{c.operation}</td>
                  <td>{c.language}</td>
                  <td>{c.kind}</td>
                  <td>{c.source}</td>
                  <td>{c.active ? 'yes' : 'no'}</td>
                  <td className="text-right">
                    <button className="text-xs underline" disabled={busy} onClick={() => void toggleCase(c)}>
                      {c.active ? 'park' : 'activate'}
                    </button>{' '}
                    <button className="text-xs underline" onClick={() => setCaseForm({ key: c.key, operation: c.operation, language: c.language, kind: c.kind, instructions: c.instructions, fixture: c.fixture, expectations: JSON.stringify(c.expectations, null, 2), source: c.source, note: c.note ?? '' })}>
                      edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className={CARD}>
          <div className="text-sm font-semibold">30-day trend</div>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-1">Day</th>
                {models.map((m) => (
                  <th key={m} className="text-right">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trendByDay.map(({ day, points }) => (
                <tr key={day} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1">{day}</td>
                  {models.map((m) => {
                    const p = points.find((x) => x.model === m);
                    return (
                      <td key={m} className="py-1 text-right tabular-nums" title={p ? classes(p.failure_classes) : ''}>
                        {p ? `${fmtPct(p.deterministic_pass_pct)} / ${fmtScore(p.judge_avg)}` : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {trendByDay.length === 0 && (
                <tr>
                  <td className="py-2 text-xs text-gray-500" colSpan={models.length + 1}>No run in the last 30 days.</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="mt-1 text-xs text-gray-500">pass rate / judge score per day and model</div>
        </div>

        <div className={CARD}>
          <div className="text-sm font-semibold">Runs</div>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-1">Started</th>
                <th>Trigger</th>
                <th>Models</th>
                <th>Cases</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-800 cursor-pointer ${r.id === selectedRunId ? 'bg-gray-50 dark:bg-gray-800/40' : ''}`} onClick={() => setParams({ run: r.id })} data-testid={`run-${r.id}`}>
                  <td className="py-1">{fmtWhen(r.started_at)}</td>
                  <td>{r.trigger}</td>
                  <td className="text-xs">{r.models.join(', ')}</td>
                  <td>{r.case_count}</td>
                  <td>{r.status}{r.error ? ` (${r.error.slice(0, 60)})` : ''}</td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td className="py-2 text-xs text-gray-500" colSpan={5}>No run yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {run && (
        <div className={CARD}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold">
              Run {run.run.id.slice(0, 8)} - {fmtWhen(run.run.started_at)} - {run.run.trigger} - judge {run.run.judge_model ?? 'none'} - case set {run.run.case_set_version ?? '-'}{run.run.reasoning_effort ? ` - reasoning ${run.run.reasoning_effort} (pinned)` : ''} - {run.run.source === 'core-tenant' ? `through core ${run.run.core_version ?? '?'} (what a customer gets)` : 'gateway-direct (raw prompts)'}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <select className={INPUT} value={modelFilter} onChange={(e) => setModelFilter(e.target.value)}>
                <option value="">all models</option>
                {run.run.models.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={failedOnly} onChange={(e) => setFailedOnly(e.target.checked)} /> failed only
              </label>
              <button className={BTN} disabled={busy || run.run.status !== 'done'} title="Re-run the judge stage on the stored outputs with the current judge prompt and cross-check (OCP-705)" onClick={() => void rejudge(run.run.id)}>
                Rejudge
              </button>
              <select className={INPUT} value={diffWith} onChange={(e) => setDiffWith(e.target.value)} disabled={!modelFilter} title={modelFilter ? 'Compare with another run' : 'Pick a model first'}>
                <option value="">diff with...</option>
                {runs.filter((r) => r.id !== run.run.id && r.status === 'done').map((r) => (
                  <option key={r.id} value={r.id}>{fmtWhen(r.started_at)}</option>
                ))}
              </select>
            </div>
          </div>

          {diff && (
            <div className="mt-3 rounded border border-gray-200 dark:border-gray-800 p-2">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Diff ({modelFilter}): {diff.filter((d) => d.changed).length} of {diff.length} cases moved</div>
              <table className="mt-1 w-full text-xs">
                <tbody>
                  {diff.filter((d) => d.changed).map((d) => (
                    <tr key={d.case_key} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1 font-mono">{d.case_key}</td>
                      <td>pass {String(d.a_pass)} to {String(d.b_pass)}</td>
                      <td>judge {fmtScore(d.a_judge)} to {fmtScore(d.b_judge)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-1">Case</th>
                <th>Model</th>
                <th>Pass</th>
                <th>Judge</th>
                <th>Invented</th>
                <th>Failure classes</th>
                <th>Finish</th>
                <th className="text-right">ms</th>
              </tr>
            </thead>
            <tbody>
              {run.results.map((r) => (
                <>
                  <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-800 cursor-pointer ${r.deterministic_pass ? '' : 'text-red-700 dark:text-red-300'}`} onClick={() => setOpenResult(openResult === r.id ? null : r.id)} data-testid={`result-${r.case_key}-${r.model}`}>
                    <td className="py-1 font-mono text-xs">{r.case_key}</td>
                    <td className="text-xs">{r.model}</td>
                    <td>{r.ok ? (r.deterministic_pass ? 'yes' : 'no') : 'error'}</td>
                    <td className="tabular-nums">{fmtScore(r.judge_score)}</td>
                    <td className="text-xs">{r.invented.join(', ') || '-'}</td>
                    <td className="text-xs">
                      <ClassChips fc={r.failure_classes} legend={legend} />
                      {r.source && r.source !== 'gateway-direct' && (
                        <div className="mt-0.5 text-[10px] text-gray-500" data-testid={`core-path-${r.case_key}`}>
                          {r.core_route}{r.core_mode ? ` (${r.core_mode})` : ''}
                          {r.core_guard
                            ? (r.core_guard.languageRetried || r.core_guard.structureRetried || r.core_guard.groundingStripped || r.core_guard.groundingRetried)
                              ? ` - core repaired: ${[r.core_guard.languageRetried ? `language retry${r.core_guard.detectedLanguage ? ` (${r.core_guard.detectedLanguage})` : ''}` : null, r.core_guard.structureRetried ? 'structure re-ask' : null, r.core_guard.groundingStripped ? 'ungrounded values stripped' : null, r.core_guard.groundingRetried ? 'grounding retry' : null].filter(Boolean).join(', ')}`
                              : ' - no guard correction'
                            : ' - guard unknown'}
                        </div>
                      )}
                    </td>
                    <td className={`text-xs ${r.finish_reason === 'length' ? 'text-red-700 dark:text-red-300' : ''}`}>{r.finish_reason ?? '-'}</td>
                    <td className="text-right tabular-nums">{r.latency_ms ?? '-'}</td>
                  </tr>
                  {openResult === r.id && (
                    <tr key={`${r.id}-detail`} className="bg-gray-50 dark:bg-gray-800/40">
                      <td colSpan={8} className="p-2">
                        {r.error && <div className="text-xs text-red-700 dark:text-red-300">Gateway error: {r.error}</div>}
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Checks</div>
                        <ul className="text-xs">
                          {Object.entries(r.checks).map(([name, c]) => (
                            <li key={name}>
                              {c.pass ? 'ok' : 'FAIL'} {name}{c.detail ? `: ${c.detail}` : ''}
                            </li>
                          ))}
                        </ul>
                        {r.judge_claims && (
                          <div className="mt-1 text-xs">
                            Judge{r.judge_claims.prompt_version ? ` (${r.judge_claims.prompt_version})` : ''}: {r.judge_claims.supported} supported, {r.judge_claims.unsupported.length} unsupported
                            {(r.judge_claims.unverified ?? 0) > 0 ? `, ${r.judge_claims.unverified} unverified` : ''}
                            {(r.judge_claims.overruled ?? 0) > 0 ? `, ${r.judge_claims.overruled} overruled` : ''}
                            {(r.judge_claims.inference ?? 0) > 0 ? `, ${r.judge_claims.inference} inference` : ''}
                            {r.judge_claims.notes ? ` - ${r.judge_claims.notes}` : ''}{r.judge_claims.error ? ` - ${r.judge_claims.error}` : ''}
                            {r.judge_claims.claims && r.judge_claims.claims.length > 0 ? (
                              <ul className="mt-1" data-testid={`judge-claims-${r.case_key}`}>
                                {r.judge_claims.claims.map((c, i) => (
                                  <li key={i}>
                                    <span className={`rounded px-1 text-[10px] uppercase tracking-wide ${c.verdict === 'unsupported' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' : c.verdict === 'supported' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>{c.verdict}</span>{' '}
                                    {c.text}
                                    {c.evidence ? <span className="text-gray-500"> - quote: "{c.evidence}"</span> : null}
                                    {c.why ? <span className="text-gray-500"> - {c.why}</span> : null}
                                  </li>
                                ))}
                              </ul>
                            ) : r.judge_claims.unsupported.length > 0 ? ` (${r.judge_claims.unsupported.join(' | ')})` : null}
                          </div>
                        )}
                        <div className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-500">Output</div>
                        <pre className="whitespace-pre-wrap text-xs">{r.output ?? '(pruned)'}</pre>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
